/**
 * BAR-192 Phase E — the verdict review drawer ("transition window").
 *
 * Lists every adjudicated finding of the current seal as a proposed
 * transition (was amber → verdict, with rationale + source quote) and lets
 * the user accept/reject each, or bulk-accept per group. Decisions persist
 * as workspace events (submitted by the parent via onDecide); accepting a
 * confirmed verdict reveals the Fix button — Phase F's entry point, which
 * here dispatches a pre-filled chat instruction (never auto-fires).
 *
 * Renders only when the parent has an adjudicated, non-stale seal — with
 * no verdicts there is nothing to review and the button never shows.
 */

import React, { useMemo, useState } from 'react';
import { ChevronDown, X, Check, Undo2, Wrench } from 'lucide-react';
import {
  GROUNDING_ACTION_LINES,
  GROUNDING_CARD_FOOTER,
  groundingCardCopy,
} from './groundingCardCopy';
import type {
  GroundingReview,
  GroundingAdjudication,
  ReportGroundingSeal,
} from '../../types/workspaceState';
import type { GroundingReviewDecisionInput } from '../../apiService';
import type { GroundingRepairedRow } from '../../stores/projectStore';

export interface ReviewRow {
  /** Stable per-render key. */
  id: string;
  kind: string;
  line?: number;
  detail: string;
  adjudication?: GroundingAdjudication;
  /** Raw (pre-review) severity from the seal — the drawer groups on this. */
  severity: 'confirmed' | 'cleared' | 'suspected';
  review?: GroundingReview;
  identity: GroundingReviewDecisionInput | null;
}

/** Flatten the seal's four itemized collections into drawer rows.
 *  Exported for tests. */
export function groundingReviewRows(seal: ReportGroundingSeal): ReviewRow[] {
  const rows: ReviewRow[] = [];
  const identity = (
    kind: string,
    verdict: string | undefined,
    decision: 'accepted' | 'rejected',
    lineSha1?: string,
    markerId?: string,
  ): GroundingReviewDecisionInput | null => {
    if (!verdict) return null;
    if (kind === 'dangling_reference') {
      return markerId ? { kind, verdict, decision, marker_id: markerId } : null;
    }
    return lineSha1 ? { kind, verdict, decision, line_sha1: lineSha1 } : null;
  };
  // The decision value is filled at click time — store a template with
  // 'accepted' and let the caller override.
  for (const c of seal.presence?.uncited_claims ?? []) {
    rows.push({
      id: `unc:${c.line}:${c.line_sha1 ?? ''}`,
      kind: 'uncited_claim',
      line: c.line,
      detail: `Uncited claim: "${c.text}"`,
      adjudication: c.adjudication,
      severity: (c.severity ?? 'suspected') as ReviewRow['severity'],
      review: c.review,
      identity: identity('uncited_claim', c.adjudication?.verdict, 'accepted', c.line_sha1),
    });
  }
  for (const f of seal.correspondence_findings ?? []) {
    rows.push({
      id: `corr:${f.line}:${f.line_sha1 ?? ''}`,
      kind: f.kind || 'correspondence',
      line: f.line,
      detail: f.detail || '',
      adjudication: f.adjudication,
      severity: (f.severity ?? 'suspected') as ReviewRow['severity'],
      review: f.review,
      identity: identity(f.kind || 'correspondence', f.adjudication?.verdict, 'accepted', f.line_sha1),
    });
  }
  for (const f of seal.adjudication?.sweep_findings ?? []) {
    const sha = (f as { line_sha1?: string }).line_sha1;
    rows.push({
      id: `sweep:${f.line}:${sha ?? ''}`,
      kind: f.kind || 'semantic',
      line: f.line,
      detail: f.detail || '',
      adjudication: f.adjudication,
      severity: 'confirmed',
      review: f.review,
      identity: identity(f.kind || 'semantic', f.adjudication?.verdict, 'accepted', sha),
    });
  }
  for (const [markerId, d] of Object.entries(seal.adjudication?.dangling ?? {})) {
    rows.push({
      id: `dangling:${markerId}`,
      kind: 'dangling_reference',
      detail: `Citation {ref:${markerId}} does not resolve to any retrieved source — the reference may be fabricated.`,
      adjudication: { verdict: d.verdict, rationale: d.rationale, quote: d.quote },
      severity: (d.severity ?? 'confirmed') as ReviewRow['severity'],
      review: d.review,
      identity: identity('dangling_reference', d.verdict, 'accepted', undefined, markerId),
    });
  }
  return rows;
}

const chip = (cls: string, text: string, struck = false) => (
  <span
    className={`inline-block mr-1.5 px-1 py-px rounded text-[10px] font-semibold uppercase tracking-wide ${cls} ${
      struck ? 'line-through opacity-70' : ''
    }`}
  >
    {text}
  </span>
);

function RowView({
  row,
  submitting,
  onDecide,
  onFix,
  onFindSource,
  onInternal,
  sealFresh = false,
}: {
  row: ReviewRow;
  submitting: boolean;
  onDecide: (d: GroundingReviewDecisionInput) => void;
  onFix?: (row: ReviewRow) => void;
  onFindSource?: (row: ReviewRow) => void;
  onInternal?: (row: ReviewRow) => void;
  /** True when the current seal is a FRESH adjudication (not carried) —
   *  an in-flight fix whose finding is still here after a fresh run has
   *  honestly failed ("still flagged"). */
  sealFresh?: boolean;
}) {
  const isSweep = row.kind.startsWith('semantic_');
  const decided = row.review?.decision;
  // Phase F: unverifiable ambers (suspected + adjudication) are actionable
  // too — Find source / Internal instead of Accept/Reject.
  const decidable = row.identity != null;
  const decideAs = (decision: 'accepted' | 'rejected') =>
    row.identity && onDecide({ ...row.identity, decision });

  // Same shape as the report hover card: one plain line for the state, one
  // plain line per button, and the checker's own wording folded away.
  const [detailsOpen, setDetailsOpen] = useState(false);
  const copy = groundingCardCopy({
    severity: row.severity,
    verdict: row.adjudication?.verdict,
    decision: decided,
  });
  const detailsId = `review-row-details-${row.id}`;
  const actionLines: string[] = [];
  if (decidable) {
    if (decided === 'fix_requested') {
      if (onFix) actionLines.push(GROUNDING_ACTION_LINES.fix);
    } else if (decided === 'source_requested') {
      if (onFindSource) actionLines.push(GROUNDING_ACTION_LINES.findSource);
      if (onInternal) actionLines.push(GROUNDING_ACTION_LINES.internal);
    } else if (decided === 'accepted_internal') {
      // Only "change" is on show, which undoes the decision rather than
      // starting new work.
    } else if (decided) {
      if (decided === 'accepted' && row.severity === 'confirmed' && onFix) {
        actionLines.push(GROUNDING_ACTION_LINES.fix);
      }
    } else if (row.severity === 'suspected') {
      if (onFindSource) actionLines.push(GROUNDING_ACTION_LINES.findSource);
      if (onInternal) actionLines.push(GROUNDING_ACTION_LINES.internal);
    } else {
      actionLines.push(GROUNDING_ACTION_LINES.reject);
    }
  }

  return (
    <div
      data-testid="review-row"
      className="border border-border-default rounded-md px-3 py-2 mb-2 bg-surface"
    >
      {copy ? (
        <div
          data-testid="review-row-headline"
          className="text-xs font-semibold text-fg whitespace-normal"
        >
          {copy.headline}
        </div>
      ) : null}
      {copy?.subline ? (
        <div
          data-testid="review-row-subline"
          className="text-[11px] text-fg-secondary mt-0.5 whitespace-normal"
        >
          {copy.subline}
        </div>
      ) : null}
      {decidable && (
        <div className="flex items-center gap-2 mt-1.5">
          {decided === 'fix_requested' ? (
            <>
              <span
                data-testid="review-inflight-fix"
                className={`text-[11px] font-medium ${sealFresh ? 'text-danger' : 'text-fg-tertiary'}`}
              >
                {sealFresh
                  ? '✗ still flagged after validation — the fix did not land'
                  : 'fix in progress — verifies on next validation'}
              </span>
              {onFix ? (
                <button
                  type="button"
                  data-testid="review-fix-retry-button"
                  disabled={submitting}
                  onClick={() => onFix(row)}
                  className="inline-flex items-center gap-1 h-6 px-2 rounded-md text-[11px] font-medium bg-primary text-on-primary hover:opacity-90 disabled:opacity-50"
                >
                  <Wrench size={11} /> Fix again
                </button>
              ) : null}
            </>
          ) : decided === 'source_requested' ? (
            <>
              <span data-testid="review-inflight-source" className="text-[11px] font-medium text-fg-tertiary">
                {sealFresh
                  ? 'still unverifiable after retrieval — consider marking internal'
                  : 'searching for source — resolves on next validation'}
              </span>
              {onFindSource ? (
                <button
                  type="button"
                  data-testid="review-find-source-retry-button"
                  disabled={submitting}
                  onClick={() => onFindSource(row)}
                  className="inline-flex items-center gap-1 h-6 px-2 rounded-md text-[11px] font-medium bg-primary text-on-primary hover:opacity-90 disabled:opacity-50"
                >
                  Search again
                </button>
              ) : null}
              {onInternal ? (
                <button
                  type="button"
                  data-testid="review-internal-button"
                  disabled={submitting}
                  onClick={() => onInternal(row)}
                  className="inline-flex items-center gap-1 h-6 px-2 rounded-md text-[11px] text-fg-secondary border border-border-default hover:bg-neutral-warm disabled:opacity-50"
                >
                  Internal
                </button>
              ) : null}
            </>
          ) : decided === 'accepted_internal' ? (
            <>
              <span data-testid="review-internal-label" className="text-[11px] font-medium text-fg-tertiary">
                internal — not a regulatory claim
              </span>
              <button
                type="button"
                data-testid="review-undo-internal"
                disabled={submitting}
                onClick={() => decideAs('accepted')}
                className="inline-flex items-center gap-1 h-6 px-2 rounded-md text-[11px] text-fg-tertiary border border-border-default hover:bg-neutral-warm disabled:opacity-50"
                title="Bring it back as a flagged item"
              >
                <Undo2 size={11} /> change
              </button>
            </>
          ) : decided ? (
            <>
              <span
                data-testid="review-decision-label"
                className={`inline-flex items-center gap-1 text-[11px] font-medium ${
                  decided === 'accepted' ? 'text-success' : 'text-fg-tertiary'
                }`}
              >
                {decided === 'accepted' ? (
                  <>
                    <Check size={16} strokeWidth={1.5} aria-hidden="true" />
                    accepted by you
                  </>
                ) : (
                  <>dismissed by you</>
                )}
              </span>
              {decided === 'accepted' && row.severity === 'confirmed' && onFix ? (
                <button
                  type="button"
                  data-testid="review-fix-button"
                  disabled={submitting}
                  onClick={() => onFix(row)}
                  className="inline-flex items-center gap-1 h-6 px-2 rounded-md text-[11px] font-medium bg-primary text-on-primary hover:opacity-90 disabled:opacity-50"
                >
                  <Wrench size={11} /> Fix
                </button>
              ) : null}
              <button
                type="button"
                data-testid="review-undo-button"
                disabled={submitting}
                onClick={() => decideAs(decided === 'accepted' ? 'rejected' : 'accepted')}
                className="inline-flex items-center gap-1 h-6 px-2 rounded-md text-[11px] text-fg-tertiary border border-border-default hover:bg-neutral-warm disabled:opacity-50"
                title={decided === 'accepted' ? 'Change to reject' : 'Change to accept'}
              >
                <Undo2 size={11} /> change
              </button>
            </>
          ) : row.severity === 'suspected' ? (
            <>
              {onFindSource ? (
                <button
                  type="button"
                  data-testid="review-find-source-button"
                  disabled={submitting}
                  onClick={() => onFindSource(row)}
                  className="inline-flex items-center gap-1 h-6 px-2 rounded-md text-[11px] font-medium bg-primary text-on-primary hover:opacity-90 disabled:opacity-50"
                >
                  Find source
                </button>
              ) : null}
              {onInternal ? (
                <button
                  type="button"
                  data-testid="review-internal-button"
                  disabled={submitting}
                  onClick={() => onInternal(row)}
                  className="inline-flex items-center gap-1 h-6 px-2 rounded-md text-[11px] text-fg-secondary border border-border-default hover:bg-neutral-warm disabled:opacity-50"
                  title="Not a regulatory claim — stop flagging it"
                >
                  Internal
                </button>
              ) : null}
            </>
          ) : (
            <>
              <button
                type="button"
                data-testid="review-accept-button"
                disabled={submitting}
                onClick={() => decideAs('accepted')}
                className="inline-flex items-center gap-1 h-6 px-2 rounded-md text-[11px] font-medium bg-success-bg text-success border border-success/40 hover:opacity-90 disabled:opacity-50"
              >
                <Check size={11} /> Accept
              </button>
              <button
                type="button"
                data-testid="review-reject-button"
                disabled={submitting}
                onClick={() => decideAs('rejected')}
                className="inline-flex items-center gap-1 h-6 px-2 rounded-md text-[11px] font-medium bg-warning-bg text-warning border border-warning/40 hover:opacity-90 disabled:opacity-50"
              >
                <X size={11} /> Reject
              </button>
            </>
          )}
        </div>
      )}
      {actionLines.map((line) => (
        <div
          key={line}
          data-testid="review-action-line"
          className="text-[11px] text-fg-secondary mt-0.5 whitespace-normal"
        >
          {line}
        </div>
      ))}
      <button
        type="button"
        data-testid="review-row-details-toggle"
        aria-expanded={detailsOpen}
        aria-controls={detailsId}
        onClick={() => setDetailsOpen((v) => !v)}
        className="inline-flex items-center gap-1 mt-1.5 text-[11px] font-medium text-fg-tertiary hover:text-fg-secondary"
      >
        <ChevronDown
          size={11}
          aria-hidden="true"
          className={detailsOpen ? '' : '-rotate-90'}
        />
        Details
      </button>
      <div hidden={!detailsOpen} id={detailsId} data-testid="review-row-details"
        className="text-xs leading-relaxed text-fg-secondary whitespace-normal mt-1">
        {row.severity === 'cleared' ? (
          <>
            {chip('bg-warning-bg text-warning', row.kind.replace(/_/g, ' '), true)}
            {chip('bg-success-bg text-success', 'cleared')}
          </>
        ) : isSweep ? (
          <>
            {chip('bg-danger-bg text-danger border border-danger/30', 'new · deep check')}
            {chip('bg-danger-bg text-danger', row.adjudication?.verdict ?? 'confirmed')}
          </>
        ) : row.severity === 'confirmed' ? (
          <>
            {chip('bg-warning-bg text-warning', 'flagged', true)}
            {chip('bg-danger-bg text-danger', row.adjudication?.verdict ?? row.kind.replace(/_/g, ' '))}
          </>
        ) : (
          chip('bg-warning-bg text-warning', row.kind.replace(/_/g, ' '))
        )}
        {row.line ? <span className="text-fg-tertiary mr-1">L{row.line}</span> : null}
        <span className={row.severity === 'cleared' ? 'line-through opacity-60' : ''}>
          {row.detail}
        </span>
        {row.adjudication?.rationale && row.adjudication.rationale !== row.detail ? (
          <span className="block mt-0.5 text-fg-secondary">{row.adjudication.rationale}</span>
        ) : null}
        {row.adjudication?.quote ? (
          <span className="block mt-0.5 pl-2 border-l-2 border-danger/40 text-fg-tertiary italic">
            source: “{row.adjudication.quote}”
          </span>
        ) : null}
      </div>
    </div>
  );
}

export default function GroundingReviewDrawer({
  seal,
  repaired = [],
  submitting,
  error,
  onClose,
  onDecide,
  onFix,
  onFindSource,
  onInternal,
  onBulkFix,
}: {
  seal: ReportGroundingSeal;
  repaired?: GroundingRepairedRow[];
  submitting: boolean;
  error?: string | null;
  onClose: () => void;
  onDecide: (decisions: GroundingReviewDecisionInput[]) => void;
  onFix?: (row: ReviewRow) => void;
  onFindSource?: (row: ReviewRow) => void;
  onInternal?: (row: ReviewRow) => void;
  onBulkFix?: (rows: ReviewRow[]) => void;
}) {
  const rows = useMemo(() => groundingReviewRows(seal), [seal]);
  const sealFresh = Boolean(seal.adjudication?.ran && !seal.adjudication?.carried);
  // Auto-repair: a surviving red the nit-judge ruled a refinement nit. It
  // leaves the Confirmed group and lands in "Minor (auto-resolved)" with an
  // override that re-surfaces it as a real issue (submits `accepted`).
  const minor = rows.filter((r) => r.review?.decision === 'good_enough');
  const confirmed = rows.filter(
    (r) => r.severity === 'confirmed' && r.review?.decision !== 'good_enough',
  );
  const cleared = rows.filter((r) => r.severity === 'cleared' && r.review?.decision !== 'accepted');
  const resolved = rows.filter((r) => r.severity === 'cleared' && r.review?.decision === 'accepted');
  const internal = rows.filter(
    (r) => r.severity === 'suspected' && r.review?.decision === 'accepted_internal',
  );
  const unverifiable = rows.filter(
    (r) => r.severity === 'suspected' && r.review?.decision !== 'accepted_internal',
  );
  const fixableNow = confirmed.filter(
    (r) => r.review?.decision === 'accepted' && r.identity,
  );

  const bulk = (group: ReviewRow[]) => {
    const undecided = group
      .filter((r) => !r.review && r.identity)
      .map((r) => ({ ...(r.identity as GroundingReviewDecisionInput), decision: 'accepted' as const }));
    if (undecided.length) onDecide(undecided);
  };

  const groupHeader = (title: string, group: ReviewRow[], bulkable: boolean) => (
    <div className="flex items-center justify-between mt-3 mb-1.5">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-fg-tertiary">
        {title} ({group.length})
      </span>
      {bulkable && group.some((r) => !r.review && r.identity) ? (
        <button
          type="button"
          data-testid={`review-bulk-accept-${title.toLowerCase().split(' ')[0]}`}
          disabled={submitting}
          onClick={() => bulk(group)}
          className="text-[11px] font-medium text-success hover:underline disabled:opacity-50"
        >
          Accept all
        </button>
      ) : null}
    </div>
  );

  return (
    <div
      data-testid="grounding-review-drawer"
      className="absolute top-0 right-0 h-full w-[420px] max-w-[85%] z-30 bg-surface border-l border-border-default shadow-bark-md flex flex-col"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-default">
        <div>
          <div className="text-sm font-semibold text-fg">Validation review</div>
          <div className="text-[11px] text-fg-tertiary">
            Accept or reject what validation found — decisions are saved.
          </div>
          {/* The card's footer, stated once for the whole drawer rather
              than repeated under every row in a scrolling list. */}
          <div data-testid="review-drawer-footer" className="text-[10px] text-fg-tertiary mt-0.5">
            {GROUNDING_CARD_FOOTER}
          </div>
        </div>
        <button
          type="button"
          aria-label="Close review"
          data-testid="review-close-button"
          onClick={onClose}
          className="h-6 w-6 inline-flex items-center justify-center rounded-md text-fg-tertiary hover:bg-neutral-warm"
        >
          <X size={14} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-2">
        {error ? (
          <div className="text-[11px] text-danger bg-danger-bg border border-danger/40 rounded-md px-2 py-1.5 mb-2">
            {error}
          </div>
        ) : null}
        {confirmed.length > 0 && (
          <>
            <div className="flex items-center justify-between mt-3 mb-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-fg-tertiary">
                Confirmed issues ({confirmed.length})
              </span>
              <span className="inline-flex items-center gap-2">
                {onBulkFix && fixableNow.length > 1 ? (
                  <button
                    type="button"
                    data-testid="review-bulk-fix"
                    disabled={submitting}
                    onClick={() => onBulkFix(fixableNow)}
                    className="text-[11px] font-medium text-primary hover:underline disabled:opacity-50"
                  >
                    Fix all accepted ({fixableNow.length})
                  </button>
                ) : null}
                {confirmed.some((r) => !r.review && r.identity) ? (
                  <button
                    type="button"
                    data-testid="review-bulk-accept-confirmed"
                    disabled={submitting}
                    onClick={() => bulk(confirmed)}
                    className="text-[11px] font-medium text-success hover:underline disabled:opacity-50"
                  >
                    Accept all
                  </button>
                ) : null}
              </span>
            </div>
            {confirmed.map((r) => (
              <RowView key={r.id} row={r} submitting={submitting} onDecide={(d) => onDecide([d])} onFix={onFix} sealFresh={sealFresh} />
            ))}
          </>
        )}
        {cleared.length > 0 && (
          <>
            {groupHeader('Cleared', cleared, true)}
            {cleared.map((r) => (
              <RowView key={r.id} row={r} submitting={submitting} onDecide={(d) => onDecide([d])} />
            ))}
          </>
        )}
        {unverifiable.length > 0 && (
          <>
            {groupHeader('Could not verify', unverifiable, false)}
            {unverifiable.map((r) => (
              <RowView
                key={r.id}
                row={r}
                submitting={submitting}
                onDecide={(d) => onDecide([d])}
                onFindSource={onFindSource}
                onInternal={onInternal}
                sealFresh={sealFresh}
              />
            ))}
          </>
        )}
        {repaired.length > 0 && (
          <>
            {groupHeader('Repaired', repaired as unknown as ReviewRow[], false)}
            {repaired.map((r) => (
              <div
                key={r.identityKey}
                data-testid="review-repaired-row"
                className="border border-border-default rounded-md px-3 py-2 mb-2 bg-surface"
              >
                <span className="text-xs leading-relaxed text-fg-secondary">
                  {chip(
                    r.outcome === 'fixed' || r.outcome === 'resolved'
                      ? 'bg-success-bg text-success'
                      : 'bg-warning-bg text-warning',
                    r.outcome === 'fixed'
                      ? '✓ fixed'
                      : r.outcome === 'resolved'
                        ? '✓ resolved'
                        : 'edited',
                  )}
                  {chip('bg-neutral-warm text-fg-tertiary', r.kind.replace(/_/g, ' '))}
                  {r.outcome === 'edited'
                    ? 'Sentence changed — re-validate to confirm the fix.'
                    : r.outcome === 'resolved'
                      ? 'No longer flagged after the source retrieval.'
                      : 'The issue is gone from the latest validation.'}
                </span>
              </div>
            ))}
          </>
        )}
        {internal.length > 0 && (
          <>
            {groupHeader('Internal', internal, false)}
            {internal.map((r) => (
              <RowView key={r.id} row={r} submitting={submitting} onDecide={(d) => onDecide([d])} sealFresh={sealFresh} />
            ))}
          </>
        )}
        {resolved.length > 0 && (
          <>
            {groupHeader('Resolved', resolved, false)}
            {resolved.map((r) => (
              <RowView key={r.id} row={r} submitting={submitting} onDecide={(d) => onDecide([d])} />
            ))}
          </>
        )}
        {minor.length > 0 && (
          <>
            {groupHeader('Minor (auto-resolved)', minor, false)}
            {minor.map((r) => (
              <div
                key={r.id}
                data-testid="review-minor-row"
                className="px-2 py-1.5 rounded-md border border-border-default mt-1"
              >
                <div className="text-[11px] text-fg-tertiary">
                  {r.line ? `Line ${r.line} — ` : ''}judged a minor refinement, not a real issue
                </div>
                {r.detail ? (
                  <div className="text-xs text-fg-secondary mt-0.5">{r.detail}</div>
                ) : null}
                {r.identity ? (
                  <button
                    type="button"
                    data-testid="review-minor-override-button"
                    disabled={submitting}
                    onClick={() => onDecide([r.identity as GroundingReviewDecisionInput])}
                    className="mt-1 inline-flex items-center h-5 px-1.5 rounded text-[10px] font-medium text-warning border border-warning/40 hover:bg-warning-bg disabled:opacity-50"
                  >
                    This actually matters
                  </button>
                ) : null}
              </div>
            ))}
          </>
        )}
        {rows.length === 0 && (
          <div className="text-xs text-fg-tertiary mt-4">Nothing to review.</div>
        )}
      </div>
    </div>
  );
}
